import { useRef, type FC } from 'react';

import { Accordion, AccordionContent, AccordionItem } from '@repo/ui/components/common';

import type { MachineGroup, MachineSubgroup } from '@/shared/types/machine.types';
import { isPrice } from '@/shared/utils/number.utils';

import { SipAccordionTrigger } from '@/components/accordion/SipAccordionTrigger';
import { MachineTable } from '@/components/machine/MachineTable';
import { MachineTableBody } from '@/components/machine/MachineTableBody';
import { MachineTableHeader } from '@/components/machine/MachineTableHeader';

interface MachineGroupAccordionProps {
  group: MachineGroup;
  selectedSubgroup?: MachineSubgroup | null;
  onSubgroupChange: (subgroup: MachineSubgroup | null) => void;
}

export const MachineGroupAccordion: FC<MachineGroupAccordionProps> = ({
  group,
  selectedSubgroup,
  onSubgroupChange,
}) => {
  const ref = useRef<HTMLDivElement>(null);
  return (
    <div className="col-span-full">
      <Accordion
        className="mb-10 w-full"
        type="single"
        collapsible
        ref={ref}
        value={selectedSubgroup?.id.toString() ?? ''}
        onValueChange={(value) => {
          const subgroup = group.subgroups?.find((s) => s.id.toString() === value) ?? null;
          onSubgroupChange(subgroup);
          ref.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }}
      >
        {group.subgroups.map((subgroup) => {
          const hasPrice =
            subgroup.sidegroups?.some((sidegroup) =>
              sidegroup.machines?.some((machine) => isPrice(machine.price) && machine.price > 0),
            ) ?? false;

          return (
            <AccordionItem key={subgroup.id} value={subgroup.id.toString()} className="border-0">
              <SipAccordionTrigger className="data-[state=open]:rounded-b-none">
                {subgroup.title}
              </SipAccordionTrigger>
              <AccordionContent className="p-0">
                <MachineTable>
                  <MachineTableHeader
                    isSelected={selectedSubgroup?.id === subgroup.id}
                    hasPrice={hasPrice ?? false}
                  />
                  {subgroup.sidegroups?.map((sidegroup) => (
                    <MachineTableBody
                      key={sidegroup.id}
                      sidegroup={sidegroup}
                      hasPrice={hasPrice}
                    />
                  ))}
                </MachineTable>
              </AccordionContent>
            </AccordionItem>
          );
        })}
      </Accordion>
    </div>
  );
};
